import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StepperStep {
  /** Translated label shown next to the step marker */
  label: React.ReactNode;
}

export interface StepperProps {
  /** Accessible label for the step list (translated by the caller) */
  ariaLabel: string;
  /** Optional additional CSS classes */
  className?: string;
  /** 1-based index of the currently active step */
  currentStep: number;
  /** Ordered steps to display */
  steps: StepperStep[];
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic numbered-circle stepper: check icon once a step is done, ring on the
 * active step, progress connector between steps. Arbitrary step count and
 * i18n-agnostic — labels and the accessible list name come in as props.
 */
export function Stepper({ ariaLabel, className, currentStep, steps }: StepperProps): React.JSX.Element {
  return (
    <ol aria-label={ariaLabel} className={cn('flex items-center justify-center gap-2 px-2', className)}>
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isDone = stepNumber < currentStep;
        const isActive = stepNumber === currentStep;

        return (
          <li aria-current={isActive ? 'step' : undefined} className="flex items-center gap-2" key={stepNumber}>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors',
                  isDone || isActive
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground',
                  isActive && 'ring-4 ring-primary/20',
                )}
              >
                {isDone ? <Check aria-hidden="true" className="h-4 w-4" /> : stepNumber}
              </span>
              <span
                className={cn(
                  'text-xs sm:text-sm',
                  isActive ? 'font-semibold text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className="relative h-0.5 w-8 overflow-hidden rounded-full bg-border sm:w-10">
                <div
                  className={cn('absolute inset-y-0 start-0 bg-primary transition-all duration-500', {
                    'w-0': !isDone,
                    'w-full': isDone,
                  })}
                />
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
